import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { JapanSimDataMeta } from '../../types/domain';
import { PrefectureShapes } from '../../utils/SimViewer/prefectureShapes';
import styled from 'styled-components';
import { useMetaData } from '../../hooks/data/useMetaData';

type Props = {
  selectedPeriod: {
    year: number;
    month: number;
    dayType: 'weekday' | 'holiday';
  } | null;
  hoveredPrefectureCode: string | null;
  setHoveredPrefectureCode: (prefectureCode: string | null) => void;
};

const JapanPolygons = ({
  selectedPeriod,
  hoveredPrefectureCode,
  setHoveredPrefectureCode,
}: Props): ReactNode => {
  const navigate = useNavigate();
  const { japanDataMetas } = useMetaData();

  return (
    <CenteredSVGWrapper>
      <svg width="auto" height="100%" viewBox="0 -40 840 800">
        <defs>
          <linearGradient
            id="selectGradient"
            x1="0%"
            y1="100%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#004ba6" />
            <stop offset="50%" stopColor="#00c1d0" />
            <stop offset="100%" stopColor="#c6dc10" />
          </linearGradient>
        </defs>
        {PrefectureShapes.map((polygon, index): ReactNode => {
          // 各都道府県の該当するデータを取得する
          const simDataMeta = Array.from(japanDataMetas.values()).find(
            (data: JapanSimDataMeta): boolean => {
              return (
                data.prefectureCode === polygon.code &&
                data.year === selectedPeriod?.year &&
                data.month === selectedPeriod?.month &&
                data.dayType === selectedPeriod?.dayType
              );
            }
          );

          const isAvailable = !!simDataMeta;
          const isHovered = polygon.code === hoveredPrefectureCode;

          return (
            <path
              key={index}
              d={polygon.path}
              fill={
                isAvailable
                  ? isHovered
                    ? 'url(#selectGradient)'
                    : '#ffffff'
                  : '#707070'
              }
              stroke="#d9d9d9"
              strokeWidth={0.5}
              onMouseEnter={
                isAvailable
                  ? (): void => {
                      setHoveredPrefectureCode(polygon.code);
                    }
                  : undefined
              }
              onMouseLeave={
                isAvailable
                  ? (): void => {
                      setHoveredPrefectureCode(null);
                    }
                  : undefined
              }
              onClick={
                isAvailable
                  ? (): void => {
                      navigate(`/sim-viewer/${simDataMeta.simdata_id}`);
                    }
                  : undefined
              }
              style={{
                cursor: isAvailable ? 'pointer' : 'not-allowed',
                opacity: isAvailable ? 1 : 0.5,
              }}
            />
          );
        })}
        <g>
          <polyline
            id="divider"
            points="461.48,738.5 461.48,678.87 714.51,416.26 829.859,416.26 "
            fill="none"
            stroke="#d9d9d9"
            strokeWidth={1}
          />
        </g>
      </svg>
    </CenteredSVGWrapper>
  );
};

const CenteredSVGWrapper = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export { JapanPolygons };
